import { createStore } from 'zustand';

import { AgreementType } from '@MP/shared/api/hooks/patient';
import type { PatientCreateRequest } from '@MP/shared/api/hooks/patient';

/** ViewModel이 소비하는 데이터 접근 계층. 컴포넌트가 React Query 훅으로 구현체를 조립해 생성 시 1회 주입한다. */
export interface PatientInvitationRepository {
  checkPhoneExists: (phoneNumber: string) => Promise<boolean>;
  sendInvitation: (body: PatientCreateRequest) => Promise<void>;
}

/** 폼에서 받는 원시 입력값. DTO(PatientCreateRequest) 조립은 ViewModel이 담당한다. */
export interface PatientInvitationFields {
  orgId: number;
  code: string;
  name: string;
  phoneNumber: string;
  birth: string;
}

export interface PatientInvitationState {
  isSubmitting: boolean;
  openWarning: boolean;
  closeWarning: () => void;
  /** 발송 성공 시 생성된 요청 DTO, 전화번호 중복(경고 오픈)이면 null을 반환한다. */
  submit: (fields: PatientInvitationFields) => Promise<PatientCreateRequest | null>;
  confirmDuplicate: (params: {
    fields: PatientInvitationFields;
    result: boolean;
  }) => Promise<PatientCreateRequest | null>;
  reset: () => void;
}

const buildRequest = (fields: PatientInvitationFields): PatientCreateRequest => ({
  orgId: fields.orgId,
  code: fields.code,
  name: fields.name,
  phoneNumber: fields.phoneNumber,
  birthYear: parseInt(fields.birth.substring(0, 4), 10),
  birthMonth: parseInt(fields.birth.substring(4, 6), 10),
  birthDay: parseInt(fields.birth.substring(6, 8), 10),
  agreements: [{ type: AgreementType.PatientRegistration }],
});

const createPatientInvitationStore = (repo: PatientInvitationRepository) =>
  createStore<PatientInvitationState>((set) => {
    const commit = async (fields: PatientInvitationFields) => {
      set({ isSubmitting: true });
      try {
        const newUser = buildRequest(fields);
        await repo.sendInvitation(newUser);
        return newUser;
      } finally {
        set({ isSubmitting: false });
      }
    };

    return {
      isSubmitting: false,
      openWarning: false,
      closeWarning: () => set({ openWarning: false }),
      submit: async (fields) => {
        set({ isSubmitting: true });
        try {
          const exists = await repo.checkPhoneExists(fields.phoneNumber);
          if (exists) {
            set({ openWarning: true });
            return null;
          }
          const newUser = buildRequest(fields);
          await repo.sendInvitation(newUser);
          return newUser;
        } finally {
          set({ isSubmitting: false });
        }
      },
      confirmDuplicate: async ({ fields, result }) => {
        set({ openWarning: false });
        if (!result) {
          return null;
        }
        return commit(fields);
      },
      reset: () => set({ isSubmitting: false, openWarning: false }),
    };
  });

export const PatientInvitationViewModel = {
  createStore: createPatientInvitationStore,
};
